<script setup lang="ts">
import {
  albumDetailPlaceholderHeights,
  albumListPlaceholderHeights
} from '~/utils/masonry.mjs'

const props = withDefaults(defineProps<{
  variant?: 'list' | 'detail' | 'info'
  count?: number
  startIndex?: number
  single?: boolean
  columns: number
}>(), {
  variant: 'list',
  count: 6,
  startIndex: 0
})

const placeholderHeights = computed(() =>
  props.variant === 'list'
    ? albumListPlaceholderHeights
    : albumDetailPlaceholderHeights
)

// cardStyle 按基准宽度换算固定比例，保持各布局下的瀑布流形状
const cardStyle = (index: number) => ({
  aspectRatio: `320 / ${placeholderHeights.value[(props.startIndex + index - 1) % placeholderHeights.value.length]}`
})
</script>

<template>
  <div
    :class="['album-skeleton', `album-skeleton-${props.variant}`, { 'album-skeleton-dense': props.columns === 3 }]"
    :style="{ '--album-layout-columns': props.single ? 1 : props.columns }"
    aria-hidden="true"
  >
    <div v-if="props.variant === 'info'" class="card album-skeleton-head">
      <span class="skeleton-block skeleton-back" />
      <span class="skeleton-block skeleton-heading" />
      <div class="skeleton-meta-row">
        <span class="skeleton-block skeleton-meta" />
        <span class="skeleton-block skeleton-meta" />
        <span class="skeleton-block skeleton-meta" />
      </div>
      <span class="skeleton-block skeleton-description" />
    </div>
    <div
      v-else
      :class="props.variant === 'list'
        ? 'album-skeleton-columns'
        : 'album-skeleton-detail-columns'"
    >
      <article
        v-for="index in props.count"
        :key="index"
        class="card album-skeleton-card"
        :style="cardStyle(index)"
      >
        <div class="album-skeleton-cover skeleton-block" />
        <div class="album-skeleton-overlay">
          <template v-if="props.variant === 'list'">
            <span class="skeleton-block skeleton-card-title" />
            <span class="skeleton-block skeleton-card-description" />
            <div class="skeleton-card-meta-row">
              <span class="skeleton-block skeleton-card-meta" />
              <span class="skeleton-block skeleton-card-meta skeleton-card-meta-short" />
            </div>
          </template>
          <template v-else>
            <span class="skeleton-block skeleton-detail-description" />
            <span class="skeleton-block skeleton-detail-description skeleton-detail-description-short" />
          </template>
        </div>
      </article>
    </div>
  </div>
</template>

<style scoped>
.album-skeleton {
  display: grid;
  gap: 14px;
}

.album-skeleton-head {
  display: grid;
  gap: 12px;
  padding: 20px;
}

.album-skeleton-columns {
  column-count: var(--album-layout-columns);
  column-gap: 14px;
}

.album-skeleton-detail-columns {
  column-count: var(--album-layout-columns);
  column-gap: 12px;
}

.album-skeleton-card {
  position: relative;
  display: block;
  width: 100%;
  margin-bottom: 14px;
  break-inside: avoid;
  overflow: hidden;
}

.album-skeleton-detail .album-skeleton-card {
  margin-bottom: 12px;
}

.album-skeleton-card:last-child {
  margin-bottom: 0;
}

.album-skeleton-cover.skeleton-block {
  position: absolute;
  inset: 0;
  border-radius: 0;
}

.album-skeleton-overlay {
  position: absolute;
  inset: auto 0 0;
  display: grid;
  gap: var(--album-skeleton-overlay-gap, 9px);
  padding: var(--album-skeleton-overlay-padding, 42px 16px 16px);
  background: linear-gradient(180deg, transparent, color-mix(in srgb, var(--bg) 82%, transparent));
}

.skeleton-card-title {
  width: 58%;
  height: var(--album-skeleton-title-height, 24px);
}

.skeleton-card-description {
  display: var(--album-skeleton-description-display, block);
  width: 88%;
  height: 12px;
}

.skeleton-detail-description {
  width: 82%;
  height: 12px;
}

.skeleton-detail-description-short {
  width: 55%;
}

.skeleton-card-meta-row {
  display: flex;
  gap: 10px;
}

.skeleton-card-meta {
  width: var(--album-skeleton-meta-width, 86px);
  height: var(--album-skeleton-meta-height, 12px);
}

.skeleton-card-meta-short {
  width: var(--album-skeleton-meta-short-width, 62px);
}

.album-skeleton-list.album-skeleton-dense {
  --album-skeleton-overlay-gap: 6px;
  --album-skeleton-overlay-padding: 32px 12px 12px;
  --album-skeleton-title-height: 18px;
  --album-skeleton-meta-width: 45%;
  --album-skeleton-meta-height: 11px;
  --album-skeleton-meta-short-width: 32%;
  --album-skeleton-description-display: none;
}

html[data-album-list-layout] .albums-page > .album-skeleton-list {
  --album-skeleton-overlay-gap: 9px;
  --album-skeleton-overlay-padding: 42px 16px 16px;
  --album-skeleton-title-height: 24px;
  --album-skeleton-meta-width: 86px;
  --album-skeleton-meta-height: 12px;
  --album-skeleton-meta-short-width: 62px;
  --album-skeleton-description-display: block;
}

html[data-album-list-layout='relaxed'] .albums-page > .album-skeleton-list .album-skeleton-card:nth-child(n + 7),
html[data-album-detail-layout='relaxed'] .album-detail-page > .album-skeleton-detail .album-skeleton-card:nth-child(n + 7),
html[data-album-list-layout='immersive'] .albums-page > .album-skeleton-list .album-skeleton-card:nth-child(n + 4),
html[data-album-detail-layout='immersive'] .album-detail-page > .album-skeleton-detail .album-skeleton-card:nth-child(n + 4) {
  display: none;
}

@media (min-width: 641px) {
  html[data-album-list-layout='relaxed'] .albums-page > .album-skeleton-list .album-skeleton-columns,
  html[data-album-detail-layout='relaxed'] .album-detail-page > .album-skeleton-detail .album-skeleton-detail-columns {
    column-count: 2;
  }

  html[data-album-detail-layout='immersive'] .album-detail-page > .album-skeleton-detail .album-skeleton-detail-columns,
  html[data-album-list-layout='immersive'] .albums-page > .album-skeleton-list .album-skeleton-columns {
    column-count: 1;
  }
}

.skeleton-back {
  width: 92px;
  height: 16px;
}

.skeleton-heading {
  width: min(280px, 58%);
  height: 36px;
}

.skeleton-meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.skeleton-meta {
  width: 112px;
  height: 14px;
}

.skeleton-description {
  width: min(520px, 80%);
  height: 16px;
}

.skeleton-block {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  background: var(--album-skeleton-background);
}

.skeleton-block::after {
  content: '';
  position: absolute;
  inset: 0;
  background: var(--album-skeleton-shimmer);
  background-size: 200% 100%;
  background-repeat: no-repeat;
  animation: blog-image-skeleton var(--album-skeleton-duration) linear infinite;
}

@media (max-width: 640px) {
  .album-skeleton-columns,
  .album-skeleton-detail-columns {
    column-count: 1;
  }
}
</style>
